import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import Card from "../../../UI/Card/Card";
import Button from "../../../UI/Button/Button"
import ErrorPanel, { ErrorSize } from "../../../UI/ErrorPanel/ErrorPanel";

import { faEdit } from "@fortawesome/free-solid-svg-icons";

import * as ApplicationsUtils from "../../../Utils/ApplicationsUtils";
import * as ContactUtils from "../../../Utils/ContactUtils";
import * as ErrorUtils from "../../../Utils/ErrorUtils";
import * as ApplicationsActions from "../../../../middleware/Store/Actions/ApplicationsActions";
import * as SandBoxApplicationsActions from "../../../../middleware/Store/Actions/SandBoxApplicationsActions";
import SelectMultiColumns from "../../../UI/Select/SelectMultiColumns";

import LogService from "../../../../middleware/CommonSdk/LogService";
const LOG_TAG = "APP-EDITION-CHANGE-OWNER";

const ChangeOwner = (props) => {

    const intl = useIntl();
    const [ownerId, setOwnerId] = useState("");
    const [application, setApplication] = useState(null);
    const [progress, setProgress] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect application");
        if (props.applicationId) {
            const foundApplication = ApplicationsUtils.getApplicationById(props.applicationId, props.applications);
            setApplication(foundApplication);
            if (foundApplication) {
                setOwnerId(foundApplication.ownerId);
            }

            if (props.applicationId !== application?.id) {
                setProgress(false);
                setErrorMessage("");
            }
        }
    }, [props.applications, props.applicationId]);

    const handleUpdate = (event) => {
        LogService.trace(LOG_TAG, "handleUpdate", event.target.value);
        const criteria = {
            ownerId: ownerId
        };

        setProgress(true);
        props.update(application?.id, criteria)
            .then(() => {
                LogService.debug(LOG_TAG, "handleUpdate success");
                setProgress(false);
                if (props.onActionSuccess) {
                    props.onActionSuccess();
                }
            })
            .catch((errorPayload) => {
                LogService.debug(LOG_TAG, "handleUpdate error");
                setProgress(false);
                setErrorMessage(ErrorUtils.getPrettyErrorMessage(errorPayload, intl));
                if (props.onActionFailed) {
                    props.onActionFailed();
                }
            });
    };

    const handleOwnerChange = (contact) => {
        setOwnerId(contact.id);
    };

    LogService.trace(LOG_TAG, "render");

    const enableOwnerChange = props.isUpdateEnabled && (ApplicationsUtils.isBusinessApplication(application) || ApplicationsUtils.isInternalApplication(application));

    let developerList = ContactUtils.getDevelopers([...props.contacts, props.user]);
    if (ApplicationsUtils.isInternalApplication(application))
        developerList = ContactUtils.getInternalDevelopers([...props.contacts, props.user]);
    let selectionList = ContactUtils.sortByCompanyAndName(developerList);

    const foundDeveloper = ContactUtils.getContactById(ownerId, [...props.contacts, props.user]);
    const isStillDeveloper = ContactUtils.isDeveloper(foundDeveloper);
    if (!isStillDeveloper && foundDeveloper) {
        selectionList.push(foundDeveloper);
    }

    let ownerSelection = <SelectMultiColumns
        itemList={selectionList}
        firstColumnDisplayFunction={(contact) => ContactUtils.getDisplayName(contact)}
        onItemSelection={handleOwnerChange}
        selectedItem={foundDeveloper}
        disabled={!enableOwnerChange}
        defaultValue={ownerId}
        dropdownClassName="dropdown-menu-right"
    />

    if (ContactUtils.isSuperAdminOrAppSuperAdmin(props.user)) {
        ownerSelection = <SelectMultiColumns
            itemList={selectionList}
            firstColumnDisplayFunction={(contact) => ContactUtils.getDisplayName(contact)}
            secondColumnDisplayFunction={(contact) => contact?.companyName}
            onItemSelection={handleOwnerChange}
            selectedItem={foundDeveloper}
            disabled={!enableOwnerChange}
            defaultValue={ownerId}
            dropdownClassName="dropdown-menu-right"
        />
    }

    return (
        <Card title={intl.messages.applicationEditionOwnerChangeTitle} header={props.showHeader}>
            <div className="form-group">
                <label htmlFor="owner" className="input-label">{intl.messages.applicationEditionOwnerChangeOwnerTitle}</label>
                {ownerSelection}
                {!isStillDeveloper &&
                    <div className="mt-2">
                        <ErrorPanel size={ErrorSize.Small} >{intl.messages.applicationEditionOwnerChangeIsNoLongerDeveloperMessage}</ErrorPanel>
                    </div>
                }
            </div>
            {enableOwnerChange &&
                <div>
                    <Button
                        label={intl.messages.applicationEditionOwnerChangeUpdateButton}
                        className="btn-submit"
                        icon={faEdit}
                        onClick={handleUpdate}
                        spinner={progress}
                        disabled={progress}
                    />
                    {errorMessage &&
                        <div className="mt-2">
                            <ErrorPanel size={ErrorSize.Small} >{errorMessage}</ErrorPanel>
                        </div>
                    }
                </div>
            }
        </Card>
    );
}

ChangeOwner.propTypes = {
    applicationId: PropTypes.string,
    isUpdateEnabled: PropTypes.bool,
    onActionSuccess: PropTypes.func,
    onActionFailed: PropTypes.func,
    showHeader: PropTypes.bool,
    isSandBoxApplication: PropTypes.bool
};

ChangeOwner.defaultProps = {
    applicationId: "",
    isUpdateEnabled: false,
    onActionSuccess: null,
    onActionFailed: null,
    showHeader: true,
    isSandBoxApplication: false
}

const mapStateToProps = (state, props) => {
    return {
        user: props.isSandBoxApplication ? state.sandBoxContacts.user : state.contacts.user,
        applications: props.isSandBoxApplication ? state.sandBoxApplications.applications : state.applications.applications,
        contacts: props.isSandBoxApplication ? state.sandBoxContacts.contacts : state.contacts.contacts,
    };
};

const mapDispatchToProps = (dispatch, props) => {
    return {
        update: (appId, criteria) => dispatch(props.isSandBoxApplication ? SandBoxApplicationsActions.updateApplication(appId, criteria) : ApplicationsActions.updateApplication(appId, criteria))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ChangeOwner);